/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { OperationType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TransportReasonProps {
  id: string;
  name: string;
  operationType: OperationType | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TransportReasonRecord extends Record<TransportReasonProps>({
  id: null as any,
  name: null as any,
  operationType: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    operationType: OperationType | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TransportReasonRecord {
    if (json == null) {
      throw new Error('TransportReasonRecord.fromJS: parameter cannot be null');
    }
    return new TransportReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TransportReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'TransportReasonJson.name'),
      operationType: json.operationType,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    operationType: OperationType | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TransportReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('TransportReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TransportReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TransportReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'TransportReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationType: tryOrDefault(
        () => json.operationType,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TransportReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('TransportReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TransportReasonViewModelBase implements TransportReasonProps {
  protected readonly _props: TransportReasonProps;
  private _json?: TransportReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get operationType(): OperationType | null {
    return this._props.operationType;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isGroundRun() {
    return this._props.operationType === OperationType.GroundRun;
  }
  public get isMaintenance() {
    return this._props.operationType === OperationType.Maintenance;
  }
  public get isNonPatientShuttle() {
    return this._props.operationType === OperationType.NonPatientShuttle;
  }
  public get isPatientShuttle() {
    return this._props.operationType === OperationType.PatientShuttle;
  }
  public get isPublicRelations() {
    return this._props.operationType === OperationType.PublicRelations;
  }
  public get isPublicUtility() {
    return this._props.operationType === OperationType.PublicUtility;
  }
  public get isReposition() {
    return this._props.operationType === OperationType.Reposition;
  }
  public get isResidence() {
    return this._props.operationType === OperationType.Residence;
  }
  public get isSceneCall() {
    return this._props.operationType === OperationType.SceneCall;
  }
  public get isSearchAndAssist() {
    return this._props.operationType === OperationType.SearchAndAssist;
  }
  public get isTraining() {
    return this._props.operationType === OperationType.Training;
  }
  public get isTransfer() {
    return this._props.operationType === OperationType.Transfer;
  }
  public get isTransferCenter() {
    return this._props.operationType === OperationType.TransferCenter;
  }

  public isAnyOperationType(...operationType: OperationType[]) {
    return this._props.operationType != null && operationType.includes(this._props.operationType);
  }

  constructor(props: TransportReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        operationType: this._props.operationType,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTransportReasonEntities = (state: AppState) =>
  state.entityStores.transportReason.entities;

export const selectTransportReasonById = (id: string) => (state: AppState) =>
  state.entityStores.transportReason.entities.get(id);

export const selectTransportReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.transportReason.meta.get(id);

export const selectTransportReasonAndMetadataById = (id: string) =>
  createSelector(
    selectTransportReasonById(id),
    selectTransportReasonMetadataById(id),
    (transportReason, meta) => ({ transportReason, meta }),
  );

export function selectTransportReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTransportReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TransportReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTransportReasonEntitiesByName(name: string) {
  return createSelector(selectAllTransportReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectTransportReasonEntitiesByOperationType(operationType: OperationType) {
  return createSelector(selectAllTransportReasonEntities, (entities) =>
    entities.filter((e) => e.operationType === operationType).valueSeq(),
  );
}
//#endregion Selectors
